import { Link, useLocation } from "@tanstack/react-router";
import { Phone, Mail, MapPin } from "lucide-react";

const FacebookIcon = ({ size = 16 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M13.5 21v-8h2.7l.4-3.1h-3.1V7.9c0-.9.3-1.5 1.6-1.5h1.6V3.6c-.3 0-1.2-.1-2.3-.1-2.2 0-3.8 1.4-3.8 3.9v2.5H7.9V13h2.7v8h2.9z" />
  </svg>
);

const InstagramIcon = ({ size = 16 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="3" y="3" width="18" height="18" rx="5" />
    <circle cx="12" cy="12" r="4" />
    <circle cx="17.5" cy="6.5" r="1" fill="currentColor" stroke="none" />
  </svg>
);
import logoWhiteImg from "@/assets/logo-white.svg";
import { CONTACT, COPYRIGHT_LINE } from "@/config/contact";
import { openCookieSettings } from "@/components/CookieConsent";

export function Footer() {
  const location = useLocation();
  const cleanPath = location.pathname.replace(/\/$/, "") || "/";
  if (cleanPath === "/login" || cleanPath === "/reset-password" || cleanPath.startsWith("/admin")) {
    return null;
  }

  return (
    <footer className="bg-dark-blue">
      <div className="mx-auto max-w-7xl px-6 py-16">
        <div className="grid gap-12 md:grid-cols-2 lg:grid-cols-4">
          <div>
            <img src={logoWhiteImg} alt="AutoXChange" width={140} height={140} className="h-12 w-auto" loading="lazy" />
            <p className="mt-5 text-sm leading-relaxed text-white/75">
              {CONTACT.description}
            </p>
            <div className="mt-6 flex items-center gap-3">
              <a
                href={CONTACT.social.facebook}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="AutoXChange på Facebook"
                className="flex h-9 w-9 items-center justify-center rounded-full border border-white/30 text-white/75 transition-colors hover:border-accent hover:text-accent"
              >
                <FacebookIcon size={16} />
              </a>
              <a
                href={CONTACT.social.instagram}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="AutoXChange på Instagram"
                className="flex h-9 w-9 items-center justify-center rounded-full border border-white/30 text-white/75 transition-colors hover:border-accent hover:text-accent"
              >
                <InstagramIcon size={16} />
              </a>
            </div>
          </div>

          <div>
            <h3 className="mb-5 text-xs font-bold uppercase tracking-widest text-brand-badge-text">Udforsk</h3>
            <nav className="flex flex-col gap-3">
              <Link to="/" className="text-sm text-white/75 hover:text-accent transition-colors">Forside</Link>
              <Link to="/biler-til-salg" className="text-sm text-white/75 hover:text-accent transition-colors">Biler til salg</Link>
              <Link to="/blog" className="text-sm text-white/75 hover:text-accent transition-colors">Blog</Link>
              <Link to="/om-os" className="text-sm text-white/75 hover:text-accent transition-colors">Om os</Link>
            </nav>
          </div>

          <div>
            <h3 className="mb-5 text-xs font-bold uppercase tracking-widest text-brand-badge-text">Information</h3>
            <nav className="flex flex-col gap-3">
              <Link to="/kontakt" className="text-sm text-white/75 hover:text-accent transition-colors">Kontakt</Link>
              <Link to="/handelsbetingelser" className="text-sm text-white/75 hover:text-accent transition-colors">Handelsbetingelser</Link>
              <Link to="/privatliv" className="text-sm text-white/75 hover:text-accent transition-colors">Privatliv & cookies</Link>
            </nav>
          </div>

          <div>
            <h3 className="mb-5 text-xs font-bold uppercase tracking-widest text-brand-badge-text">Kontakt</h3>
            <div className="flex flex-col gap-3">
              <a href={CONTACT.phoneHref} className="flex items-center gap-2 text-sm text-white/75 hover:text-accent transition-colors">
                <Phone size={14} />
                <span>{CONTACT.phoneDisplay}</span>
              </a>
              <a href={CONTACT.emailHref} className="flex items-center gap-2 text-sm text-white/75 hover:text-accent transition-colors">
                <Mail size={14} />
                <span>{CONTACT.email}</span>
              </a>
              <span className="flex items-center gap-2 text-sm text-white/75">
                <MapPin size={14} />
                <span>{CONTACT.addressLine}</span>
              </span>
            </div>
          </div>
        </div>

        <div className="mt-12 flex flex-col items-center gap-3 border-t border-white/10 pt-8 text-center text-xs text-white/70 sm:flex-row sm:justify-between sm:gap-2 sm:text-left">
          <span>{COPYRIGHT_LINE(2026)}</span>
          <button
            type="button"
            onClick={openCookieSettings}
            className="text-white/75 transition-colors hover:text-accent"
          >
            Cookie-indstillinger
          </button>
        </div>
      </div>
    </footer>
  );
}
